import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { AppFrame, BottomNav, WhatsAppFAB } from "@/components/Shell";
import WelcomeSheet from "@/components/WelcomeSheet";
import InstallPrompt from "@/components/InstallPrompt";
import { Search } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORY_IMAGES = {
  "Artificial Jewellery": "https://images.unsplash.com/photo-1705326454924-f6777522b030?w=600&h=800&fit=crop",
  "Ladies Handbags": "https://images.unsplash.com/photo-1683921590274-a83862cb11c3?w=600&h=800&fit=crop",
  "Cosmetics": "https://images.unsplash.com/photo-1625093742435-6fa192b6fb10?w=600&h=800&fit=crop",
  "Ladies Accessories": "https://images.unsplash.com/photo-1542452255191-c85a98f2c5d1?w=600&h=800&fit=crop",
};

const Section = ({ title, kicker, children, action, numeral }) => (
  <section className="px-6 mt-14">
    <div className="flex items-end justify-between mb-5">
      <div className="flex items-baseline gap-3">
        {numeral && (
          <span className="font-display-italic text-[15px]" style={{ color: "var(--accent)" }}>
            {numeral}
          </span>
        )}
        <div>
          {kicker && (
            <div className="text-[9px] tracking-editorial uppercase" style={{ color: "var(--ink-3)" }}>
              {kicker}
            </div>
          )}
          <h2 className="font-display text-[26px] leading-[1.05] mt-1" style={{ color: "var(--ink)" }}>
            {title}
          </h2>
        </div>
      </div>
      {action}
    </div>
    {children}
  </section>
);

export default function Home() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, c, r, b, f] = await Promise.all([
        api.get("/settings"),
        api.get("/categories"),
        api.get("/products", { params: { sort: "recent", limit: 6 } }),
        api.get("/products", { params: { best_seller: true, limit: 6 } }),
        api.get("/products", { params: { featured: true, limit: 6 } }),
      ]);
      setSettings(s.data);
      setCategories(c.data);
      setNewArrivals(r.data);
      setBestSellers(b.data);
      setFeatured(f.data);
    })();
  }, []);

  const topCats = categories.filter((c) => !c.parent_id);

  return (
    <AppFrame>
      {/* Brand banner — sits as the page header, palette-matched */}
      <div className="relative w-full" data-testid="brand-banner">
        <img
          src="/banner.jpg"
          alt="Butterfly The Fashion Store"
          className="w-full h-auto block select-none"
          style={{ aspectRatio: "1200 / 669" }}
          draggable={false}
        />
        {/* soft fade bleeding into the page cream so the banner doesn't feel like a boxed image */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-10"
          style={{ background: "linear-gradient(to bottom, rgba(244,237,226,0) 0%, var(--bg) 100%)" }}
        />
        {/* Floating search — sits on the banner, palette-matched */}
        <button
          data-testid="header-search-btn"
          onClick={() => navigate("/search")}
          className="absolute top-4 right-4 w-10 h-10 rounded-full grid place-items-center press backdrop-blur"
          style={{
            background: "rgba(244, 237, 226, 0.75)",
            border: "1px solid rgba(155, 70, 53, 0.25)",
            color: "var(--ink)",
          }}
          aria-label="Search"
        >
          <Search size={16} strokeWidth={1.5} />
        </button>
      </div>

      {/* Hero — editorial split with typography emphasis */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="px-6 pt-10"
      >
        <div className="text-[9px] tracking-editorial uppercase" style={{ color: "var(--accent)" }}>
          Volume 01 · Season 2026
        </div>
        <h1 className="mt-3 font-display text-[54px] leading-[0.95]" style={{ color: "var(--ink)" }}>
          Elegance,
          <br />
          <span className="font-display-italic" style={{ color: "var(--accent)" }}>uncomplicated.</span>
        </h1>
        <p className="mt-4 text-[13px] leading-relaxed max-w-[24rem]" style={{ color: "var(--ink-2)" }}>
          A curated house of adornments — artisanal jewellery, hand-picked handbags, and a considered beauty edit. Made for the woman who chooses well.
        </p>

        <div className="mt-6 flex items-center gap-3">
          <Link
            to="/search"
            data-testid="hero-shop-btn"
            className="press h-11 px-5 rounded-full text-[12px] tracking-editorial-sm uppercase flex items-center gap-2"
            style={{ background: "var(--ink)", color: "var(--bg)" }}
          >
            Enter the store
            <span aria-hidden>→</span>
          </Link>
          <a
            data-testid="hero-whatsapp-btn"
            href={`https://wa.me/${(settings?.whatsapp_number || "").replace(/[^0-9]/g, "")}`}
            target="_blank" rel="noopener noreferrer"
            className="press text-[11px] tracking-editorial-sm uppercase underline underline-offset-[6px] decoration-[1px]"
            style={{ color: "var(--ink-2)", textDecorationColor: "var(--accent)" }}
          >
            WhatsApp us
          </a>
        </div>

        {/* Editorial imagery pair */}
        <div className="mt-10 grid grid-cols-5 gap-3">
          <div className="col-span-3 aspect-[3/4] rounded-2xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1705326454924-f6777522b030?w=800&h=1000&fit=crop"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="col-span-2 flex flex-col gap-3">
            <div className="flex-1 rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1625093742435-6fa192b6fb10?w=400&h=500&fit=crop"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div
              className="flex-1 rounded-2xl p-4 flex flex-col justify-between"
              style={{ background: "var(--ink)", color: "var(--bg)" }}
            >
              <div className="text-[9px] tracking-editorial uppercase opacity-70">In store</div>
              <div className="font-display-italic text-[22px] leading-none">
                shop by
                <br />appointment
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Ornamented divider */}
      <div className="px-6 mt-14">
        <div className="rule-ornament">
          <span className="font-display-italic text-[13px]">&amp;</span>
        </div>
      </div>

      {/* Categories */}
      <Section title="The Categories" kicker="Curated Rooms" numeral="No. 01">
        <div className="grid grid-cols-2 gap-3">
          {topCats.map((c) => (
            <Link
              key={c.id}
              to={c.slug ? `/c/${c.slug}` : `/category/${c.id}`}
              data-testid={`home-category-${c.id}`}
              className="relative aspect-[4/5] rounded-2xl overflow-hidden press group"
            >
              <img
                src={c.image ? resolveImage(c.image) : (CATEGORY_IMAGES[c.name] || "https://images.unsplash.com/photo-1542452255191-c85a98f2c5d1?w=600&h=800&fit=crop")}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <div className="text-[9px] tracking-editorial uppercase text-white/80">Explore</div>
                <div className="font-display-italic text-[19px] leading-tight text-white mt-0.5">
                  {c.name}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Featured */}
      {featured.length > 0 && (
        <Section title="The Editors' Edit" kicker="Handpicked" numeral="No. 02">
          <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-6 px-6 pb-2">
            {featured.map((p, i) => (
              <div key={p.id} className="w-44 shrink-0"><ProductCard product={p} index={i} /></div>
            ))}
          </div>
        </Section>
      )}

      {/* New Arrivals */}
      <Section
        title="Just Arrived"
        kicker="Latest"
        numeral="No. 03"
        action={<Link to="/search" data-testid="home-see-all-new" className="text-[10px] tracking-editorial-sm uppercase underline underline-offset-[6px]" style={{ color: "var(--ink-2)", textDecorationColor: "var(--accent)" }}>See all</Link>}
      >
        {newArrivals.length === 0 ? (
          <EmptyProducts />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {newArrivals.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </Section>

      {/* Best Sellers */}
      {bestSellers.length > 0 && (
        <Section title="Beloved" kicker="Most cherished" numeral="No. 04">
          <div className="grid grid-cols-2 gap-3">
            {bestSellers.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </Section>
      )}

      {/* Footer */}
      <div className="mt-20 px-6 pb-4">
        <div className="rule-ornament">
          <span className="font-display-italic text-[13px]">fin.</span>
        </div>
        <div className="mt-6 text-center">
          <img
            src="/logo-full.png"
            alt="Butterfly The Fashion Store"
            className="h-14 w-auto mx-auto opacity-90"
          />
          <div className="text-[9px] tracking-editorial uppercase mt-2" style={{ color: "var(--ink-3)" }}>
            The Fashion Store · Est. 2026
          </div>
        </div>
      </div>

      <WhatsAppFAB number={settings?.whatsapp_number} message="Hi Butterfly! I'd like to browse your collection." />
      <BottomNav />
      <InstallPrompt />
      <WelcomeSheet />
    </AppFrame>
  );
}

const EmptyProducts = () => (
  <div
    className="rounded-2xl py-12 text-center"
    style={{ background: "var(--bg-alt)", border: "1px solid rgba(27,25,23,0.06)" }}
  >
    <div className="font-display-italic text-xl" style={{ color: "var(--ink)" }}>coming soon</div>
    <p className="text-xs mt-2" style={{ color: "var(--ink-2)" }}>New pieces are being curated. Check back shortly.</p>
  </div>
);
